import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/StarRating";
import VerifiedBadge from "@/components/VerifiedBadge";
import ProBadge from "@/components/ProBadge";
import FoundingMemberBadge from "@/components/FoundingMemberBadge";
import FavoriteBusinessButton from "@/components/FavoriteBusinessButton";
import { MapPin, Navigation, Quote, Flame, Heart } from "lucide-react";
import { computeOpenStatus, hasAnyHours } from "@/lib/hours";

/**
 * Yelp-style horizontal business card used by the directory feed.
 * - Large square hero image on the left
 * - Business name + rating chip + review count
 * - City • price tier • open/closed status
 * - Short review snippet with "more"
 * - Category chips
 * - Right-side red "Get Directions" CTA (only when an address is present)
 */
export default function BusinessListingCard({ business, index = 0 }) {
  const navigate = useNavigate();
  const b = business || {};
  const status = hasAnyHours(b.hours) ? computeOpenStatus(b.hours) : null;
  const hero = b.hero_image || b.logo_data_url || "";
  const initials = (b.name || "?").trim()[0]?.toUpperCase() || "?";
  const reviewCount = b.rating_count || 0;
  const reviewCountLabel = reviewCount > 999
    ? `${(reviewCount / 1000).toFixed(1).replace(/\.0$/, "")}k reviews`
    : `${reviewCount} review${reviewCount === 1 ? "" : "s"}`;
  const cityLine = [b.city, b.state].filter(Boolean).join(", ");
  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${b.name || ""} ${b.address_full || b.address || ""}`.trim()
  )}`;

  // Trending flag — the server attaches `is_trending` (composite of activity
  // signals). Fall back to the client-side heuristic when the field is missing
  // (e.g., legacy endpoints that don't call enrich_business_for_list yet).
  const sig = b.activity_signals || {};
  const recentActivity = (sig.reviews_30d || 0) + (sig.checkins_30d || 0) + (sig.photos_30d || 0);
  const isTrending = typeof b.is_trending === "boolean"
    ? b.is_trending
    : (recentActivity >= 5 || (recentActivity >= 3 && sig.has_active_promo));

  return (
    <Card
      className={`rounded-xl border border-border bg-white overflow-hidden hover:shadow-md transition-shadow cursor-pointer fade-up-${(index % 4) + 1}`}
      data-testid={`directory-card-${b.id}`}
      role="link"
      tabIndex={0}
      onClick={(e) => {
        // Only navigate when the click target isn't itself a link/button/anchor.
        const interactive = e.target.closest("a, button, [role='button']");
        if (interactive) return;
        navigate(`/businesses/${b.id}`);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/businesses/${b.id}`);
        }
      }}
      aria-label={`Open ${b.name || "business"} profile`}
    >
      <div className="flex flex-col sm:flex-row gap-0">
        {/* Hero image — links to the profile */}
        <Link
          to={`/businesses/${b.id}`}
          className="block sm:w-56 md:w-64 flex-shrink-0 bg-gray-100"
          data-testid={`directory-card-hero-${b.id}`}
        >
          {hero ? (
            <img
              src={hero}
              alt={`${b.name || "business"} hero`}
              className="w-full h-56 sm:h-full sm:aspect-square object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-56 sm:h-full sm:aspect-square flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
              <span className="font-display text-5xl text-[#B31E1E]/40">{initials}</span>
            </div>
          )}
        </Link>

        {/* Right column */}
        <div className="flex-1 p-5 sm:p-6 flex flex-col gap-3 min-w-0">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <Link to={`/businesses/${b.id}`} className="group">
                <h3 className="font-display text-2xl sm:text-3xl tracking-tighter group-hover:text-[#B31E1E] transition-colors [overflow-wrap:normal] [word-break:keep-all] hyphens-none">
                  {b.name}
                </h3>
              </Link>

              {/* Mobile-only "Get Directions" — sits right under the business name */}
              {(b.address_full || b.address) && (
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="sm:hidden mt-3 inline-block"
                  data-testid={`directory-card-directions-mobile-${b.id}`}
                >
                  <Button
                    type="button"
                    className="rounded-[35px] bg-red-600 hover:bg-red-700 shadow-sm hover:shadow-md text-white h-9 px-4 text-xs"
                  >
                    <Navigation size={13} className="mr-1.5" /> Get Directions
                  </Button>
                </a>
              )}

              <div className="mt-3 sm:mt-2 flex items-center gap-2 flex-wrap">
                {isTrending && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow-sm ring-1 ring-orange-500/40"
                    title={`Trending — ${recentActivity} interactions in the last 30 days`}
                    data-testid={`directory-card-trending-${b.id}`}
                  >
                    <Flame size={11} className="drop-shadow-sm" />
                    Trending
                  </span>
                )}
                <span
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 bg-[#f6f7f8]"
                  data-testid={`directory-card-rating-${b.id}`}
                >
                  <StarRating value={b.rating_avg} size={14} />
                  <span className="text-xs font-semibold tabular-nums text-gray-900">
                    {(b.rating_avg || 0).toFixed(1)}
                  </span>
                </span>
                <span className="text-xs text-gray-500 font-mono">{reviewCountLabel}</span>
                {b.is_pro_subscriber && <ProBadge size={14} />}
                {b.is_verified && <VerifiedBadge size={16} />}
                {b.is_founding_member && <FoundingMemberBadge />}
                {b.claimed === false && (
                  <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-amber-400 text-amber-700">
                    Unclaimed
                  </Badge>
                )}
                {(sig.favorite_count || 0) > 0 && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono text-rose-600 bg-rose-50 border border-rose-100"
                    data-testid={`directory-card-favorites-${b.id}`}
                    title={`${sig.favorite_count} customer favorite${sig.favorite_count === 1 ? "" : "s"}`}
                  >
                    <Heart size={10} fill="currentColor" />
                    {sig.favorite_count}
                  </span>
                )}
              </div>

              <p className="mt-2 flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                <MapPin size={13} className="text-gray-400" />
                <span>{cityLine || (b.address || "Address unavailable")}</span>
                {typeof b.distance_mi === "number" && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span
                      className="font-semibold text-[#B31E1E]"
                      data-testid={`directory-card-distance-${b.id}`}
                    >
                      {b.distance_mi < 0.1 ? "< 0.1 mi" : `${b.distance_mi.toFixed(1)} mi`}
                    </span>
                  </>
                )}
                {b.price_tier && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span className="font-semibold text-gray-700" data-testid={`directory-card-price-${b.id}`}>
                      {b.price_tier}
                    </span>
                  </>
                )}
                {status?.label && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span
                      className={`font-semibold ${status.open ? "text-emerald-600" : "text-rose-600"}`}
                      data-testid={`directory-card-status-${b.id}`}
                    >
                      {status.open ? "Open " : "Closed "}
                    </span>
                    <span className="text-gray-600">{status.label.replace(/^(Open|Closed)\s*/, "")}</span>
                  </>
                )}
              </p>
            </div>

            {/* Desktop-only CTA — hidden on mobile (button is inline under the name on small screens) */}
            <div className="hidden sm:flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
              <FavoriteBusinessButton businessId={b.id} initialCount={sig.favorite_count || 0} compact />
              {(b.address_full || b.address) && (
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`directory-card-directions-${b.id}`}
                >
                  <Button
                    type="button"
                    className="rounded-[35px] bg-red-600 hover:bg-red-700 shadow-sm hover:shadow-md text-white h-10 px-5"
                  >
                    <Navigation size={14} className="mr-1.5" /> Get Directions
                  </Button>
                </a>
              )}
            </div>
          </div>

          {/* Review snippet */}
          {b.top_review?.comment ? (
            <p className="text-sm text-gray-700 leading-relaxed" data-testid={`directory-card-review-${b.id}`}>
              <Quote size={13} className="inline -mt-1 mr-1 text-gray-400" />
              "{b.top_review.comment}"
              {b.top_review.comment_truncated && (
                <Link
                  to={`/businesses/${b.id}`}
                  className="ml-1 text-[#B31E1E] font-medium hover:underline"
                >
                  more
                </Link>
              )}
            </p>
          ) : b.description ? (
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{b.description}</p>
          ) : null}

          {/* Category chips */}
          {b.categories?.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid={`directory-card-categories-${b.id}`}>
              {b.categories.slice(0, 4).map((c) => (
                <Link
                  key={c}
                  to={`/directory?category=${encodeURIComponent(c)}`}
                  className="text-xs px-3 py-1 rounded-full border border-border text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {c}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
